/* eslint-disable no-console */
import './chat-container.scss';

import { useLazyQuery } from '@apollo/client';
import { last, sortBy, uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getAgendaAppointmentAppointmentInput } from '../../compositions/agenda/modals/agenda-appointment-modal/agenda-appointment-modal';
import { ChatMessageSummaryItem } from '../../compositions/conversations/modals/conversation-summary-modal/conversation-summary-modal';
import { useAppContext } from '../../data/context/app-context';
import { useConversationChatMessageItems } from '../../data/data/conversations/use-conversation-message-items';
import {
  AgendaAppointmentModalAction,
  ChatMessagePostedByType,
} from '../../data/data/enums';
import {
  AppointmentModalData,
  ChatMessageCreateFileModalData,
  ChatMessageItem,
  CreateChatDefaultModalData,
} from '../../data/data/types';
import { consultTypeToSlotType } from '../../data/data/utils';
import {
  ConversationItemDataFragment,
  GetAgendaAppointmentDocument,
  PushType,
  SlotType,
} from '../../data/types/api/graphql';
import {
  BaseSignalRMessage,
  SignalRChatMessage,
  SignalRFilterKey,
} from '../../hooks/use-signalr';
import { CustomFormattedMessage } from '../intl/custom-formatted-message';
import { Spinner } from '../spinner/spinner';
import { ChatMessage, ChatMessagePosition } from './chat-message/chat-message';
import { ChatMessageForm } from './chat-message-form/chat-message-form';

const ChatContainerPageSize = 10;

type ChatContainerProps = {
  healthProviderId: string | null | undefined;
  conversationItem: ConversationItemDataFragment | null | undefined;
  onSelectMessages?: (chatMessageItems: ChatMessageSummaryItem[]) => void;
  showAppointmentModal?: (
    slotType: SlotType,
    value: AppointmentModalData | null
  ) => void;
  showCreateChatDefaultModal?: (
    value: CreateChatDefaultModalData | null
  ) => void;
  showCreateChatFileModal?: (
    value: ChatMessageCreateFileModalData | null
  ) => void;
};

export function ChatContainer({
  healthProviderId,
  conversationItem,
  onSelectMessages,
  showAppointmentModal,
  showCreateChatDefaultModal,
  showCreateChatFileModal,
}: ChatContainerProps) {
  const messagesContainer = useRef<HTMLDivElement>(null);

  // Start in the loading state: with a per-conversation key the component
  // mounts fresh on every switch, and the first load only fires once the
  // conversation metadata resolves. Showing the spinner until then avoids a
  // flash of "no older messages" (and never an error) during the switch.
  const [loading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState<boolean>(false);
  const [messages, setMessages] = useState<ChatMessageItem[]>([]);
  const [errorLoadingMessages, setErrorLoadingMessages] =
    useState<boolean>(false);
  const [scrollToMessage, setScrollToMessage] = useState<string | null>(null);
  const [selectedMessages, setSelectedMessages] = useState<
    ChatMessageSummaryItem[]
  >([]);

  const page = useMemo(
    () => Math.ceil(messages.length / ChatContainerPageSize),
    [messages]
  );

  const { onLoad: onLoadChatMessageItemsData } =
    useConversationChatMessageItems();

  const [getAgendaAppointment] = useLazyQuery(GetAgendaAppointmentDocument, {
    fetchPolicy: 'no-cache',
  });

  const onLoadMessages = useCallback(
    async (currentPage: number) => {
      setErrorLoadingMessages(false);
      if (conversationItem?.id) {
        setLoading(true);

        const result = await onLoadChatMessageItemsData({
          id: conversationItem?.id,
          skip: ((currentPage ?? 1) - 1) * ChatContainerPageSize,
          take: ChatContainerPageSize,
        });

        // `undefined` => the request was superseded (conversation switch) or it
        // was a pincode/auth error handled globally (lock / re-login). Ignore
        // it: don't clear the messages and don't show an error. The load that
        // replaced it manages the loading state.
        if (result === undefined) {
          return;
        }

        if (result !== null) {
          setMessages(prevState => {
            const newState = sortBy(
              uniqBy([...result.items, ...prevState], option => option.id),
              option => new Date(option.postedAt)
            );
            return newState;
          });

          setPagination(result.pageInfo.hasNextPage);
        } else {
          setMessages([]);
          setErrorLoadingMessages(true);
        }
        setLoading(false);
      }
    },
    [conversationItem?.id, onLoadChatMessageItemsData]
  );

  const getReceived = useCallback(
    (item: ChatMessageItem) =>
      conversationItem?.patientLastMessageReceived
        ? new Date(item.postedAt).getTime() <=
          new Date(conversationItem.patientLastMessageReceived).getTime()
        : false,
    [conversationItem?.patientLastMessageReceived]
  );

  useEffect(() => {
    const lastMessage = last(messages);

    if (lastMessage) {
      setScrollToMessage(lastMessage.id ?? null);
    }
  }, [messages]);

  const { signalRMessage, addFilter } = useAppContext();

  const [signalRMessageId, setSignalRMessageId] = useState<{
    id: string;
    type: PushType;
  } | null>(null);

  const filter = useCallback(
    (message: BaseSignalRMessage) => {
      if (
        message &&
        [
          PushType.MessageSend,
          PushType.MessageUpdated,
          PushType.MessageReceived,
          PushType.MessageDeleted,
        ].includes(message.type)
      ) {
        const signalRItemData = message as SignalRChatMessage;
        if (signalRItemData.conversationId === conversationItem?.id) {
          return true;
        }
      }
      return false;
    },
    [conversationItem?.id]
  );

  // add filter on conversation id change
  useEffect(() => {
    if (conversationItem?.id) {
      addFilter({ key: SignalRFilterKey.CHAT, filter });
    }
  }, [addFilter, conversationItem?.id, filter]);

  // handle signalr message with filter
  useEffect(() => {
    if (signalRMessage && filter(signalRMessage)) {
      const signalRItemData = signalRMessage as SignalRChatMessage;
      console.log(
        `[${SignalRFilterKey.CHAT}] setSignalRMessageId ${signalRItemData.messageId} : ${signalRMessage.type}`
      );
      setSignalRMessageId({
        id: signalRItemData.messageId,
        type: signalRMessage.type,
      });
    }
  }, [filter, signalRMessage]);

  // delete
  useEffect(() => {
    if (signalRMessageId) {
      if (signalRMessageId.type === PushType.MessageDeleted) {
        // add lastmessage recieved
        setMessages(
          messages.filter(option => option.id !== signalRMessageId.id)
        );
      } else {
        const index = messages.findIndex(
          option => option.id === signalRMessageId.id
        );

        onLoadMessages(
          Math.ceil(index === -1 ? 1 : index / ChatContainerPageSize)
        );
      }

      setSignalRMessageId(null);
    }
  }, [signalRMessageId, messages, onLoadMessages]);

  const onSelectMessage = useCallback(
    (
      messageItem: ChatMessageItem,
      text: string | null | undefined,
      id?: string
    ) => {
      const newSelectedMessages = [...selectedMessages];

      const exists = newSelectedMessages.findIndex(
        option => option.id === (id ?? messageItem.id)
      );

      if (exists === -1) {
        newSelectedMessages.push({
          id: (id ?? messageItem.id)!,
          messageItem,
          text,
        });
      } else {
        newSelectedMessages.splice(exists, 1);
      }

      setSelectedMessages(newSelectedMessages);

      onSelectMessages?.(newSelectedMessages);
    },
    [selectedMessages, onSelectMessages]
  );

  useEffect(() => {
    if (messagesContainer.current) {
      const message = messagesContainer.current.querySelector(
        `[data-chat-message="${scrollToMessage}"]`
      );

      if (message) {
        messagesContainer.current.scrollTop =
          (message as HTMLDivElement).offsetTop +
          (message as HTMLDivElement).clientHeight;
      }
    }
  }, [scrollToMessage]);

  const onHandleScroll = useCallback(
    (e: Event) => {
      if (pagination && (e.currentTarget as HTMLDivElement).scrollTop < 50) {
        onLoadMessages(page + 1);
      }
    },
    [page, pagination, onLoadMessages]
  );

  useEffect(() => {
    const container = messagesContainer.current;

    container?.addEventListener('scroll', onHandleScroll);

    return () => {
      container?.removeEventListener('scroll', onHandleScroll);
    };
  }, [onHandleScroll]);

  useEffect(() => {
    setMessages([]);
    setScrollToMessage(null);

    onLoadMessages(1);

    setSelectedMessages([]);
  }, [conversationItem?.id, onLoadMessages]);

  const onHandleChatAppointment = useCallback(
    async (item: ChatMessageItem) => {
      if (item.appointment?.id) {
        const result = await getAgendaAppointment({
          variables: { id: item.appointment?.id },
        });
        if (result.data?.appointment) {
          showAppointmentModal?.(
            consultTypeToSlotType(result.data?.appointment?.consultType),
            {
              action: AgendaAppointmentModalAction.DEFAULT,
              healthProviderId,
              agendaAppointmentItem: getAgendaAppointmentAppointmentInput(
                result.data?.appointment
              ),
            }
          );
        }
      }
    },
    [getAgendaAppointment, healthProviderId, showAppointmentModal]
  );

  const resultMessage = useMemo(() => {
    if (loading) {
      return (
        <div className="chat-container-message chat-container-message--loading">
          <Spinner />
          <CustomFormattedMessage id="conversation.loading" />
        </div>
      );
    }

    if (!loading && !pagination && !errorLoadingMessages) {
      return (
        <div className="chat-container-message chat-container-message--no-more">
          <CustomFormattedMessage id="conversation.no-older-messages" />
        </div>
      );
    }

    if (!loading && errorLoadingMessages === true) {
      return (
        <div className="chat-container-message chat-container-message--error">
          <CustomFormattedMessage id="conversation.error-loading-messages" />
        </div>
      );
    }
    return undefined;
  }, [errorLoadingMessages, loading, pagination]);

  return (
    <div className="chat-container">
      <div
        className="chat-container__messages"
        ref={messagesContainer}
        style={{ overflowY: loading ? 'hidden' : 'auto' }}
      >
        {resultMessage}

        {messages.map(option => (
          <ChatMessage
            received={getReceived(option)}
            key={`${option.id}-${option.postedAt}`}
            position={
              option.postedBy.type === ChatMessagePostedByType.PATIENT
                ? ChatMessagePosition.LEFT
                : ChatMessagePosition.RIGHT
            }
            messageItem={option}
            active={selectedMessages
              .filter(
                selectedMessage => selectedMessage.messageItem.id === option.id
              )
              .map(selectedMessage => last(selectedMessage.id.split('.'))!)}
            removable={option.removable}
            onClickText={onSelectMessage}
            onClickAppointment={(item: ChatMessageItem) => {
              onHandleChatAppointment(item);
            }}
          />
        ))}
      </div>
      <div className="chat-container__footer">
        <ChatMessageForm
          showAppointmentModal={showAppointmentModal}
          showCreateChatDefaultModal={showCreateChatDefaultModal}
          showCreateChatFileModal={showCreateChatFileModal}
          healthProviderId={healthProviderId}
          conversationItem={conversationItem}
        />
      </div>
    </div>
  );
}
